import { get, post } from '@/lib/api/client'
import {
  PaginationParamsSchema,
  EventFilterParamsSchema,
  EventCommentCreateSchema,
  EventReplyCreateSchema,
  type Event,
  type EventDetail,
  type EventFilterParams,
  type EventComment,
  type EventCommentWithReplies,
  type EventCommentCreate,
  type EventReplyCreate,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class EventService {

  /**
   * Filter events (Public endpoint)
   * POST /events/public/filter?size={size}&page={page}
   */
  async getEvents(
    params: PaginationParams = { page: 1, size: 10 },
    filters: EventFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Event>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = EventFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      size: validatedParams.size.toString(),
      page: validatedParams.page.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Event>>>(
      `events/public/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Search events (Public endpoint)
   * GET /events/public/search?searchBy={searchTerm}&size={size}&page={page}
   */
  async searchEvents(
    searchTerm: string,
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<Event>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const query = new URLSearchParams({
      searchBy: searchTerm,
      size: validatedParams.size.toString(),
      page: validatedParams.page.toString()
    }).toString()

    return await get<ApiResponse<PaginatedResponse<Event>>>(
      `events/public/search?${query}`
    )
  }

  /**
   * Get event by ID (Public endpoint)
   * GET /events/public/{eventId}
   */
  async getEventById(eventId: string): Promise<ApiResponse<EventDetail>> {
    return await get<ApiResponse<EventDetail>>(
      `events/public/${eventId}`
    )
  }

  /**
   * Get all comments and replies for an event (Public endpoint)
   * GET /event-replies/public/all/{eventId}
   */
  async getEventCommentsWithReplies(eventId: string): Promise<ApiResponse<Array<{ eventReplyResponseDetails: EventCommentWithReplies[] }>>> {
    return await get<ApiResponse<Array<{ eventReplyResponseDetails: EventCommentWithReplies[] }>>>(
      `event-replies/public/all/${eventId}`
    )
  }

  /**
   * Add a comment to an event (Public endpoint)
   * POST /event-comments/public/addcomment/{eventId}
   */
  async addEventComment(
    eventId: string,
    commentData: EventCommentCreate
  ): Promise<ApiResponse<EventComment>> {
    const validatedData = EventCommentCreateSchema.parse(commentData)
    
    return await post<ApiResponse<EventComment>>(
      `event-comments/public/addcomment/${eventId}`,
      validatedData
    )
  }

  /**
   * Add a reply to a comment (Public endpoint)
   * POST /event-replies/public/addreply/{commentId}
   */
  async addEventReply(
    commentId: string,
    replyData: EventReplyCreate
  ): Promise<ApiResponse<EventComment>> {
    const validatedData = EventReplyCreateSchema.parse(replyData)
    
    return await post<ApiResponse<EventComment>>(
      `event-replies/public/addreply/${commentId}`,
      validatedData
    )
  }
}

export const eventService = new EventService()

